import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Toast from '../../../components/ui/Toast';
import api from '../../../utils/api';
import { getImageUrl } from '../../../utils/config';
import { createCategory, deleteCategory, updateCategory } from '../api';
import CategoryCard from '../components/CategoryCard';
import CategoryForm from '../components/CategoryForm';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';

const CategoryManagementPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    fetchStoreAndCategories();
  }, [slug]);

  const fetchStoreAndCategories = async () => {
    try {
      setLoading(true);
      // First fetch the store data using the working endpoint
      const storeResponse = await api.get(`/stores/${slug}`);
      
      if (storeResponse.data.success) {
        const storeData = storeResponse.data.data;
        setStore(storeData);
        // Use the categories from the store data
        setCategories(storeData.categories || []);
      } else {
        showToast(storeResponse.data.message || 'Failed to load store', 'error');
      }
    } catch (error) {
      console.error('Error fetching store:', error);
      const errorMessage = error.response?.data?.message || 'Failed to load store';
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async (formData) => {
    try {
      setIsSubmitting(true);
      const response = await createCategory(slug, formData);
      
      if (response.success) {
        setCategories(prev => [...prev, response.data]);
        setShowForm(false);
        showToast(response.message || 'Category created successfully', 'success');
      } else {
        showToast(response.message || 'Failed to create category', 'error');
      }
    } catch (error) {
      console.error('Error creating category:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create category';
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateCategory = async (formData) => {
    try {
      setIsSubmitting(true);
      const response = await updateCategory(editingCategory.id, formData);
      
      if (response.success) {
        setCategories(prev => 
          prev.map(cat => 
            cat.id === editingCategory.id ? response.data : cat
          )
        );
        setShowForm(false);
        setEditingCategory(null);
        showToast(response.message || 'Category updated successfully', 'success');
      } else {
        showToast(response.message || 'Failed to update category', 'error');
      }
    } catch (error) {
      console.error('Error updating category:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update category';
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deletingCategory) return;

    try {
      setIsDeleting(true);
      const response = await deleteCategory(deletingCategory.id);
      
      if (response.success) {
        setCategories(prev => prev.filter(cat => cat.id !== deletingCategory.id));
        setShowDeleteModal(false);
        setDeletingCategory(null);
        showToast(response.message || 'Category deleted successfully', 'success');
      } else {
        showToast(response.message || 'Failed to delete category', 'error');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete category';
      showToast(errorMessage, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setShowForm(true);
  };

  const handleDeleteClick = (category) => {
    setDeletingCategory(category);
    setShowDeleteModal(true);
  };

  const handleFormSubmit = (formData) => {
    if (editingCategory) {
      return handleUpdateCategory(formData);
    } else {
      return handleCreateCategory(formData);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingCategory(null);
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const closeToast = () => {
    setToast({ show: false, message: '', type: 'success' });
  };

  // Filter categories based on search term
  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading categories...</p>
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Store Not Found</h2>
          <p className="text-gray-600 mb-6">The store you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/admin/stores')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Stores
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Manage Categories: {store.name}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Create, edit, and delete categories for your store
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            <span className="mr-2">+</span>
            Add Category
          </button>
          <button
            onClick={() => navigate('/admin/stores')}
            className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
          >
            ← Back to Stores
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-gray-400">🔍</span>
          </div>
        </div>
      </div>

      {/* Categories Grid */}
      {filteredCategories.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📁</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm ? 'No categories found' : 'No categories yet'}
          </h3>
          <p className="text-gray-500 mb-6">
            {searchTerm 
              ? 'Try adjusting your search terms.' 
              : 'Get started by creating your first category.'
            }
          </p>
          {!searchTerm && (
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <span className="mr-2">+</span>
              Create First Category
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredCategories.map((category) => (
            <CategoryCard
              key={category.id}
              category={category}
              isAdmin={true}
              onEdit={handleEditCategory}
              onDelete={handleDeleteClick}
            />
          ))}
        </div>
      )}

      {/* Category Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CategoryForm
              category={editingCategory}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
              isSubmitting={isSubmitting}
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeletingCategory(null);
        }}
        onConfirm={handleDeleteCategory}
        categoryName={deletingCategory?.name}
        isDeleting={isDeleting}
      />

      {/* Toast */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.show}
        onClose={closeToast}
      />
    </div>
  );
};

export default CategoryManagementPage; 